import type { ICellRendererParams } from 'ag-grid-community';
import type { PromptExecutionHistoryBrandSummary } from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { RelationshipBadge, VisibilityBar } from '../components/visibility/VisibilityBadges';
import { formatAiUsagePercent, resolveAiMentions } from '../utils/visibility-metrics';
import { getHelpHtml } from '../utils/help/help-content';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

export interface PromptHistoryBrandsColumnCallbacks {
  totalExecutions: number;
}

export function promptHistoryBrandsColumnConfig(callbacks: PromptHistoryBrandsColumnCallbacks) {
  return [
    {
      field: 'canonicalName',
      title: 'برند',
      tooltip: columnHelp('tables.brand'),
      type: 'string',
      flex: 1,
      minWidth: 200,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span className="d-flex align-items-center gap-2">
            <BrandFavicon
              host={row.domain}
              faviconUrl={row.faviconUrl}
              name={row.canonicalName}
              relationship={row.relationship}
            />
            <span className="fw-medium text-800">{row.canonicalName}</span>
          </span>
        );
      },
    },
    {
      field: 'relationship',
      title: 'رابطه',
      tooltip: columnHelp('tables.relationship'),
      type: 'string',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) =>
        params.data ? <RelationshipBadge relationship={params.data.relationship} solid /> : null,
    },
    {
      field: 'visibilityScore',
      title: 'دیده‌شدن در AI',
      tooltip: columnHelp('metrics.ai_visibility'),
      type: 'string',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) =>
        params.data ? <VisibilityBar score={params.data.visibilityScore} /> : null,
    },
    {
      field: 'aiMentions',
      title: 'منشن در AI',
      tooltip: columnHelp('metrics.ai_mentions'),
      type: 'number',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) =>
        params.data ? <span>{resolveAiMentions(params.data)}</span> : null,
    },
    {
      field: '_aiUsage',
      title: 'استفاده در AI',
      tooltip: columnHelp('metrics.ai_usage'),
      type: 'string',
      width: 120,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) => {
        const row = params.data;
        if (!row) return null;
        return <span>{formatAiUsagePercent(resolveAiMentions(row), callbacks.totalExecutions)}</span>;
      },
    },
    {
      field: 'seenInRuns',
      title: 'پوشش اجرا',
      type: 'string',
      width: 110,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptExecutionHistoryBrandSummary>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span>
            {row.seenInRuns}/{callbacks.totalExecutions || '—'}
          </span>
        );
      },
    },
    {
      field: 'avgPosition',
      title: 'میانگین رتبه AI',
      tooltip: columnHelp('metrics.avg_ai_position'),
      type: 'string',
      width: 130,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? `#${p.value}` : '—'),
    },
  ];
}
